import { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { MatchStatus } from "service";
import { BallBase } from "components/common/BallBase";
import { SIDE } from "_constants";
import { getNormalizedStatusCode } from "utils/get-normalized-status-code";
import TennisBall from "../assets/tennis-ball.svg";
import {
  TENNIS_VIEW_H,
  TENNIS_VIEW_W,
  resolveTennisPosition,
} from "../positioning";
import {
  ServeTrailIndicator,
  ServeTrailData,
} from "../indicators/ServeTrailIndicator";

const PATH_STOPPER_EVENTS: string[] = ["1116", "1117"];
const BALL_VISIBLE_CODES = new Set<string>(["1113"]); // service events currently render the ball
const SERVE_TRAIL_DURATION_MS = 960;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

export const Ball: FC<{ status: MatchStatus }> = memo(({ status }) => {
  const computedStatus = useMemo<MatchStatus>(() => {
    const rawStatus = status as unknown as {
      x?: number;
      y?: number;
    };
    const hasX = typeof rawStatus?.x === "number";
    const hasY = typeof rawStatus?.y === "number";
    if (hasX && hasY) return status;

    const resolved = resolveTennisPosition(status);

    return {
      ...(status as unknown as Record<string, unknown>),
      x: hasX ? rawStatus?.x : resolved.x,
      y: hasY ? rawStatus?.y : resolved.y,
      side: resolved.side,
    } as MatchStatus;
  }, [status]);

  const extraDeps = [(computedStatus as any)?.sc, (computedStatus as any)?.nsc];

  const [serveTrail, setServeTrail] = useState<ServeTrailData | null>(null);
  const previousRef = useRef<{
    x?: number;
    y?: number;
    side?: SIDE;
  }>({ side: SIDE.NO_SIDE });
  const clearRef = useRef<number | null>(null);
  const normalizedCode = useMemo(
    () => getNormalizedStatusCode(computedStatus),
    [computedStatus]
  );
  const shouldRenderBall = Boolean(
    normalizedCode && BALL_VISIBLE_CODES.has(normalizedCode)
  );

  const { clampedX, clampedY, resolvedSide } = useMemo(() => {
    const raw = computedStatus as any;
    const nextX = typeof raw?.x === "number" ? clamp(raw.x, 0, 1) : undefined;
    const nextY = typeof raw?.y === "number" ? clamp(raw.y, 0, 1) : undefined;
    const rawSide = raw?.side as SIDE | undefined;
    const side =
      rawSide === SIDE.HOME || rawSide === SIDE.AWAY
        ? rawSide
        : typeof nextX === "number"
        ? nextX >= 0.5
          ? SIDE.AWAY
          : SIDE.HOME
        : SIDE.NO_SIDE;

    return { clampedX: nextX, clampedY: nextY, resolvedSide: side };
  }, [computedStatus]);

  useEffect(() => {
    if (!shouldRenderBall) {
      previousRef.current = { side: SIDE.NO_SIDE };
      setServeTrail((current) => (current ? null : current));
      return;
    }

    if (clampedX === undefined || clampedY === undefined) {
      previousRef.current = { side: SIDE.NO_SIDE };
      return;
    }

    const previous = previousRef.current;
    const previousSide: SIDE =
      previous?.side === SIDE.HOME || previous?.side === SIDE.AWAY
        ? previous.side
        : previous?.x !== undefined
        ? previous.x >= 0.5
          ? SIDE.AWAY
          : SIDE.HOME
        : SIDE.NO_SIDE;

    if (
      previous &&
      previous.x !== undefined &&
      previous.y !== undefined &&
      previousSide !== SIDE.NO_SIDE &&
      previousSide !== resolvedSide &&
      Math.abs(clampedX - previous.x) > 0.05
    ) {
      setServeTrail({
        id: Date.now(),
        from: { x: previous.x, y: previous.y },
        to: { x: clampedX, y: clampedY },
        direction: resolvedSide === SIDE.AWAY ? "right" : "left",
      });
    }

    previousRef.current = {
      x: clampedX,
      y: clampedY,
      side: resolvedSide,
    };
  }, [clampedX, clampedY, resolvedSide, shouldRenderBall]);

  useEffect(() => {
    if (!serveTrail) return;
    if (clearRef.current) {
      window.clearTimeout(clearRef.current);
    }
    clearRef.current = window.setTimeout(
      () => setServeTrail(null),
      SERVE_TRAIL_DURATION_MS
    );
    return () => {
      if (clearRef.current) {
        window.clearTimeout(clearRef.current);
        clearRef.current = null;
      }
    };
  }, [serveTrail]);

  const { homeName, awayName } = useMemo(() => {
    const raw = computedStatus as any;
    const resolveName = (
      primary?: { n?: string } | null,
      secondary?: { n?: string } | null,
      fallbackA?: string | null,
      fallbackB?: string | null
    ) => {
      const candidates: Array<string | undefined> = [
        primary?.n,
        secondary?.n,
        typeof fallbackA === "string" ? fallbackA : undefined,
        typeof fallbackB === "string" ? fallbackB : undefined,
      ];

      for (const value of candidates) {
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed.length > 0) {
            return trimmed;
          }
        }
      }

      return null;
    };

    const home = resolveName(raw?.T1, raw?.t1, raw?.T1n, raw?.t1n) ?? null;
    const away = resolveName(raw?.T2, raw?.t2, raw?.T2n, raw?.t2n) ?? null;

    return { homeName: home, awayName: away };
  }, [computedStatus]);

  const ballLabel = useMemo(() => {
    if (!shouldRenderBall) return null;
    if (resolvedSide === SIDE.HOME) return homeName;
    if (resolvedSide === SIDE.AWAY) return awayName;
    return null;
  }, [shouldRenderBall, resolvedSide, homeName, awayName]);

  const opponentLabel = useMemo(() => {
    if (!shouldRenderBall) return null;
    if (!ballLabel) return null;

    const fallbackY = clampedY ?? 0.5;

    if (resolvedSide === SIDE.HOME) {
      return awayName
        ? {
            text: awayName,
            side: SIDE.AWAY,
            x: 1,
            y: fallbackY,
            anchor: "edge" as const,
          }
        : null;
    }
    if (resolvedSide === SIDE.AWAY) {
      return homeName
        ? {
            text: homeName,
            side: SIDE.HOME,
            x: 0,
            y: fallbackY,
            anchor: "edge" as const,
          }
        : null;
    }
    return null;
  }, [shouldRenderBall, ballLabel, resolvedSide, awayName, homeName, clampedY]);

  useEffect(() => {
    return () => {
      if (clearRef.current) {
        window.clearTimeout(clearRef.current);
      }
    };
  }, []);

  return (
    <>
      {shouldRenderBall && (
        <BallBase
          status={computedStatus}
          ballImageHref={TennisBall}
          viewW={TENNIS_VIEW_W}
          viewH={TENNIS_VIEW_H}
          ballSize={10}
          pathLineMax={4}
          pathStopperEvents={PATH_STOPPER_EVENTS}
          showShadowEllipse={false}
          extraPathDeps={extraDeps}
          smoothMs={320}
          hidden={Boolean(serveTrail)}
          label={ballLabel ?? undefined}
          opponentLabel={opponentLabel ?? undefined}
        />
      )}

      {shouldRenderBall && serveTrail && (
        <ServeTrailIndicator trail={serveTrail} />
      )}
    </>
  );
});
